import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, UserCheck, UserPlus, TrendingUp, TrendingDown, DollarSign,
  Activity, Download, Filter, CalendarDays, ArrowUp, ArrowDown, Check, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COLORS = ['#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#84cc16'];

const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;

const ChangeBadge = ({ value, suffix = '' }) => {
  if (value === undefined || value === null) return null;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isZero ? 'text-gray-400' : isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
      {!isZero && (isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      {isZero ? '—' : `${Math.abs(value)}%`}{suffix}
    </span>
  );
};

const ServiceReportsPage = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [servicePopoverOpen, setServicePopoverOpen] = useState(false);

  useEffect(() => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().split('T')[0];
    setStartDate(start);
    setEndDate(end);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedServices.length > 0) params.append('service_id', selectedServices.join(','));
      const res = await api.get(`/analytics/service-breakdown?${params}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to fetch service reports');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedServices]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportCSV = () => {
    if (!data) return;
    let csv = 'Month,Service,New Patients,Returning Patients,New %,Ret %,Total Visits,Revenue,New Revenue,Returning Revenue\n';
    data.monthly_breakdown?.forEach(m => {
      m.services?.forEach(s => {
        csv += `${m.month_name},${s.service},${s.new_patients},${s.returning_patients},${s.new_pct}%,${s.returning_pct}%,${s.total_visits},${s.revenue},${s.new_revenue || 0},${s.returning_revenue || 0}\n`;
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const pieData = data?.grand_totals ? [
    { name: 'New', value: data.grand_totals.new_patients },
    { name: 'Returning', value: data.grand_totals.returning_patients }
  ] : [];

  const revPieData = data?.grand_totals ? [
    { name: 'New Rev', value: data.grand_totals.new_revenue || 0 },
    { name: 'Ret Rev', value: data.grand_totals.returning_revenue || 0 }
  ] : [];

  const getServiceTrend = () => {
    if (!data) return [];
    if (selectedServices.length === 1) {
      const serviceName = data.services_list?.find(s => s.id === selectedServices[0])?.name;
      return data.service_monthly_trend?.[serviceName] || [];
    }
    return data.monthly_trend || [];
  };

  const trendData = getServiceTrend();

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {p.name.includes('Rev') || p.name.includes('revenue') ? formatCurrency(p.value) : p.value}
          </p>
        ))}
      </div>
    );
  };

  const gt = data?.grand_totals || {};

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6 bg-gray-50 min-h-screen" data-testid="service-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Service Reports</h1>
            <p className="text-sm text-gray-500 mt-1">Monthly service-wise patient & revenue breakdown</p>
          </div>
          <Button onClick={handleExportCSV} variant="outline" className="gap-2" disabled={!data} data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-500" />
                <div>
                  <label className="text-xs text-gray-500 block">From</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" data-testid="start-date-input" />
                </div>
                <span className="text-gray-400 mt-4">—</span>
                <div>
                  <label className="text-xs text-gray-500 block">To</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" data-testid="end-date-input" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <div>
                  <label className="text-xs text-gray-500 block">Services</label>
                  <Popover open={servicePopoverOpen} onOpenChange={setServicePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[260px] justify-between text-sm font-normal" data-testid="service-filter">
                        {selectedServices.length === 0
                          ? 'All Services'
                          : selectedServices.length === 1
                            ? data?.services_list?.find(s => s.id === selectedServices[0])?.name || '1 selected'
                            : `${selectedServices.length} services selected`}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[260px] p-0" align="start">
                      <div className="p-2 border-b">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-sm"
                          onClick={() => { setSelectedServices([]); setServicePopoverOpen(false); }}
                          data-testid="clear-service-filter"
                        >
                          {selectedServices.length === 0 && <Check className="mr-2 h-4 w-4" />}
                          All Services
                        </Button>
                      </div>
                      <div className="max-h-[240px] overflow-y-auto p-1">
                        {data?.services_list?.map(s => {
                          const checked = selectedServices.includes(s.id);
                          return (
                            <label
                              key={s.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer text-sm"
                              data-testid={`service-option-${s.id}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(val) => {
                                  setSelectedServices(prev =>
                                    val ? [...prev, s.id] : prev.filter(id => id !== s.id)
                                  );
                                }}
                              />
                              {s.name}
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : data ? (
          <>
            {/* Grand Totals - 6 cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="grand-totals">
              <Card className="border-l-4 border-l-indigo-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">New Patients</p>
                  <p className="text-2xl font-bold text-indigo-700" data-testid="total-new-patients">{gt.new_patients || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-cyan-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Returning Patients</p>
                  <p className="text-2xl font-bold text-cyan-700" data-testid="total-returning-patients">{gt.returning_patients || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Visits</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="total-visits">{gt.total_visits || 0}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Revenue</p>
                  <p className="text-2xl font-bold text-amber-700" data-testid="total-revenue">{formatCurrency(gt.revenue)}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-indigo-300">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">New Patient Rev</p>
                  <p className="text-xl font-bold text-indigo-600" data-testid="new-revenue">{formatCurrency(gt.new_revenue)}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-cyan-300">
                <CardContent className="pt-4 pb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Returning Rev</p>
                  <p className="text-xl font-bold text-cyan-600" data-testid="returning-revenue">{formatCurrency(gt.returning_revenue)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2" data-testid="patient-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-500" />
                    New vs Returning Patients (Monthly)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={trendData}>
                        <defs>
                          <linearGradient id="gradNew" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradReturn" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area type="monotone" dataKey="new_patients" name="New" stroke="#6366f1" fill="url(#gradNew)" strokeWidth={2} />
                        <Area type="monotone" dataKey="returning_patients" name="Returning" stroke="#06b6d4" fill="url(#gradReturn)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">No data</p>}
                </CardContent>
              </Card>

              <Card data-testid="patient-split-pie">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-500" /> Patient Split
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          <Cell fill="#6366f1" />
                          <Cell fill="#06b6d4" />
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">No patient data</p>}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 - Revenue */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2" data-testid="revenue-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-amber-500" />
                    Revenue: New vs Returning (Monthly)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="new_revenue" name="New Rev" stackId="rev" fill="#6366f1" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="returning_revenue" name="Ret Rev" stackId="rev" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">No revenue data</p>}
                </CardContent>
              </Card>

              <Card data-testid="revenue-split-pie">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-amber-500" /> Revenue Split
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {revPieData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={revPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          <Cell fill="#6366f1" />
                          <Cell fill="#06b6d4" />
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">No revenue data</p>}
                </CardContent>
              </Card>
            </div>

            {/* Visits Trend */}
            <Card data-testid="visits-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" /> Monthly Visits Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="total_visits" name="Visits" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="text-gray-400 text-center py-10">No visit data</p>}
              </CardContent>
            </Card>

            {/* Service-wise Bar (only All Services) */}
            {data.service_summary?.length > 1 && (
              <Card data-testid="service-comparison-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-500" /> Service-wise Patient Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={Math.max(280, data.service_summary.length * 50)}>
                    <BarChart data={data.service_summary} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="service" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="new_patients" name="New" stackId="p" fill="#6366f1" />
                      <Bar dataKey="returning_patients" name="Returning" stackId="p" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Overall Service Summary Table */}
            <Card data-testid="service-summary-table">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Overall Service Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 font-semibold text-gray-600">Service</th>
                        <th className="text-right py-3 px-2 font-semibold text-indigo-600">New</th>
                        <th className="text-right py-3 px-2 font-semibold text-cyan-600">Returning</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-500">New %</th>
                        <th className="text-right py-3 px-2 font-semibold text-gray-600">Visits</th>
                        <th className="text-right py-3 px-2 font-semibold text-amber-600">Revenue</th>
                        <th className="text-right py-3 px-2 font-semibold text-orange-500">Avg/Apt</th>
                        <th className="text-right py-3 px-2 font-semibold text-indigo-500">New Rev</th>
                        <th className="text-right py-3 px-2 font-semibold text-cyan-500">Ret Rev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.service_summary?.map((s, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 px-2 font-medium text-gray-800">{s.service}</td>
                          <td className="py-2.5 px-2 text-right text-indigo-600 font-semibold">{s.new_patients}</td>
                          <td className="py-2.5 px-2 text-right text-cyan-600 font-semibold">{s.returning_patients}</td>
                          <td className="py-2.5 px-2 text-right text-gray-500">{s.new_pct}% / {s.returning_pct}%</td>
                          <td className="py-2.5 px-2 text-right text-gray-700">{s.total_visits}</td>
                          <td className="py-2.5 px-2 text-right text-amber-600 font-semibold">{formatCurrency(s.revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-orange-500 font-medium">{formatCurrency(s.avg_revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-indigo-500">{formatCurrency(s.new_revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-cyan-500">{formatCurrency(s.returning_revenue)}</td>
                        </tr>
                      ))}
                      {gt && (
                        <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                          <td className="py-2.5 px-2 text-gray-900">Total</td>
                          <td className="py-2.5 px-2 text-right text-indigo-700">{gt.new_patients}</td>
                          <td className="py-2.5 px-2 text-right text-cyan-700">{gt.returning_patients}</td>
                          <td className="py-2.5 px-2 text-right text-gray-600">
                            {gt.new_patients + gt.returning_patients > 0
                              ? `${Math.round(gt.new_patients / (gt.new_patients + gt.returning_patients) * 100)}% / ${Math.round(gt.returning_patients / (gt.new_patients + gt.returning_patients) * 100)}%`
                              : '—'}
                          </td>
                          <td className="py-2.5 px-2 text-right text-gray-900">{gt.total_visits}</td>
                          <td className="py-2.5 px-2 text-right text-amber-700">{formatCurrency(gt.revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-orange-600">{formatCurrency(gt.total_visits > 0 ? Math.round(gt.revenue / gt.total_visits) : 0)}</td>
                          <td className="py-2.5 px-2 text-right text-indigo-600">{formatCurrency(gt.new_revenue)}</td>
                          <td className="py-2.5 px-2 text-right text-cyan-600">{formatCurrency(gt.returning_revenue)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Breakdown with MoM % change */}
            <Card data-testid="monthly-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-indigo-500" /> Monthly Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.monthly_breakdown?.length > 0 ? (
                  [...data.monthly_breakdown].reverse().map((month) => {
                    const t = month.totals || {};
                    return (
                      <div key={month.month} className="border rounded-lg overflow-hidden" data-testid={`month-row-${month.month}`}>
                        <button
                          onClick={() => setExpandedMonth(expandedMonth === month.month ? null : month.month)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                          data-testid={`month-toggle-${month.month}`}
                        >
                          <span className="font-semibold text-gray-800">{month.month_name}</span>
                          <div className="flex items-center gap-3 text-xs flex-wrap justify-end">
                            <span className="text-indigo-600">New: {t.new_patients} ({t.new_pct}%)</span>
                            {t.new_change !== undefined && <ChangeBadge value={t.new_change} />}
                            <span className="text-cyan-600">Ret: {t.returning_patients} ({t.returning_pct}%)</span>
                            {t.returning_change !== undefined && <ChangeBadge value={t.returning_change} />}
                            <span className="text-amber-600">{formatCurrency(t.revenue)}</span>
                            {t.revenue_change !== undefined && <ChangeBadge value={t.revenue_change} />}
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedMonth === month.month ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </div>
                        </button>
                        {expandedMonth === month.month && (
                          <div className="px-4 pb-3 bg-gray-50 border-t">
                            {/* MoM summary */}
                            {t.new_change !== undefined && (
                              <div className="flex flex-wrap gap-4 py-2 text-xs border-b border-gray-200 mb-2">
                                <span>vs Previous Month:</span>
                                <span className="text-indigo-600">New Patients <ChangeBadge value={t.new_change} /></span>
                                <span className="text-cyan-600">Returning <ChangeBadge value={t.returning_change} /></span>
                                <span className="text-amber-600">Revenue <ChangeBadge value={t.revenue_change} /></span>
                                <span className="text-indigo-500">New Rev <ChangeBadge value={t.new_revenue_change} /></span>
                                <span className="text-cyan-500">Ret Rev <ChangeBadge value={t.returning_revenue_change} /></span>
                              </div>
                            )}
                            <table className="w-full text-sm mt-1">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-2 px-2 font-medium">Service</th>
                                  <th className="text-right py-2 px-2 font-medium text-indigo-500">New</th>
                                  <th className="text-right py-2 px-2 font-medium text-cyan-500">Returning</th>
                                  <th className="text-right py-2 px-2 font-medium">Split</th>
                                  <th className="text-right py-2 px-2 font-medium">Visits</th>
                                  <th className="text-right py-2 px-2 font-medium text-amber-500">Revenue</th>
                                  <th className="text-right py-2 px-2 font-medium text-orange-400">Avg/Apt</th>
                                  <th className="text-right py-2 px-2 font-medium text-indigo-400">New Rev</th>
                                  <th className="text-right py-2 px-2 font-medium text-cyan-400">Ret Rev</th>
                                </tr>
                              </thead>
                              <tbody>
                                {month.services?.map((s, si) => (
                                  <tr key={si} className="border-t border-gray-100">
                                    <td className="py-2 px-2 text-gray-700">{s.service}</td>
                                    <td className="py-2 px-2 text-right text-indigo-600">{s.new_patients}</td>
                                    <td className="py-2 px-2 text-right text-cyan-600">{s.returning_patients}</td>
                                    <td className="py-2 px-2 text-right text-gray-500 text-xs">{s.new_pct}% / {s.returning_pct}%</td>
                                    <td className="py-2 px-2 text-right text-gray-700">{s.total_visits}</td>
                                    <td className="py-2 px-2 text-right text-amber-600">{formatCurrency(s.revenue)}</td>
                                    <td className="py-2 px-2 text-right text-orange-500">{formatCurrency(s.avg_revenue)}</td>
                                    <td className="py-2 px-2 text-right text-indigo-500">{formatCurrency(s.new_revenue)}</td>
                                    <td className="py-2 px-2 text-right text-cyan-500">{formatCurrency(s.returning_revenue)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t-2 border-gray-300 font-semibold bg-gray-100">
                                  <td className="py-2 px-2">Month Total</td>
                                  <td className="py-2 px-2 text-right text-indigo-700">{t.new_patients}</td>
                                  <td className="py-2 px-2 text-right text-cyan-700">{t.returning_patients}</td>
                                  <td className="py-2 px-2 text-right text-gray-600 text-xs">{t.new_pct}% / {t.returning_pct}%</td>
                                  <td className="py-2 px-2 text-right">{t.total_visits}</td>
                                  <td className="py-2 px-2 text-right text-amber-700">{formatCurrency(t.revenue)}</td>
                                  <td className="py-2 px-2 text-right text-orange-600">{formatCurrency(t.avg_revenue)}</td>
                                  <td className="py-2 px-2 text-right text-indigo-600">{formatCurrency(t.new_revenue)}</td>
                                  <td className="py-2 px-2 text-right text-cyan-600">{formatCurrency(t.returning_revenue)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : <p className="text-gray-400 text-center py-8">No monthly data</p>}
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-20 text-gray-400">No data available</div>
        )}
      </div>
    </Layout>
  );
};

export default ServiceReportsPage;
